/**
 * Pre-capture image inlining.
 *
 * WHY THIS EXISTS
 * ---------------
 * html-to-image does not reuse the bitmap the browser has already
 * decoded for an <image>/<img> in the DOM. It re-FETCHES every image
 * URL from scratch and rewrites the clone's href to the resulting
 * data: URL. That second fetch is a completely different request from
 * the one that painted the logo on screen, and it can fail where the
 * original succeeded:
 *
 *   - `cacheBust: true` appends `?<timestamp>`, so it is a cache MISS
 *     by construction — a real round-trip every time, subject to
 *     whatever the network/WAF/rewrite rules do on that path.
 *   - It is a `fetch()`, not an image load, so it is bound by CORS
 *     rather than by the far laxer <img> rules.
 *   - A rewrite that answers with an HTML error page (200 + text/html)
 *     satisfies fetch but is not an image.
 *
 * When that fetch fails, html-to-image swallows the error and quietly
 * substitutes `options.imagePlaceholder` — which is how a title-block
 * logo that renders perfectly in the editor turns into a blank (or,
 * before this was fixed, a solid blue) rectangle in the exported PDF.
 * Worse, the result is memoised in a module-global cache keyed on the
 * URL minus its query string, so one failure poisons every subsequent
 * export for the life of the page.
 *
 * WHAT WE DO INSTEAD
 * ------------------
 * Before capture, walk the subtree ourselves and turn every non-data
 * image reference into a data: URL, so by the time html-to-image looks
 * at them there is nothing left to fetch (`isDataUrl` short-circuits
 * its embed path entirely). Two strategies, in order:
 *
 *   1. fetch + magic-byte sniff. Keeps vector logos vector, and the
 *      sniff means we don't trust Content-Type — extensionless files
 *      under /uploads are commonly served with no type at all, and a
 *      soft-404 HTML page is rejected rather than embedded.
 *   2. canvas raster of a fresh Image(). This is the important one:
 *      it goes through the *image* loading path, the same one that
 *      already succeeded on screen, and it reads from the browser
 *      cache. If the logo is visible in the editor this nearly always
 *      works — the only way it doesn't is cross-origin tainting, where
 *      toDataURL throws SecurityError.
 *
 * Anything that survives both is reported by URL rather than silently
 * blanked, so a missing logo shows up in the failure log instead of
 * only in a client's eyeballs.
 *
 * All mutations are reverted via the returned `restore()` — the live
 * editor DOM must look untouched once the export finishes.
 */

const XLINK_NS = 'http://www.w3.org/1999/xlink';

/** Cap on the raster fallback's longest edge. Title-block logos are
 *  drawn at ~50x30 CSS px; 2048 leaves plenty of headroom for print
 *  DPI without embedding a multi-megabyte base64 blob in the POST. */
const MAX_RASTER_PX = 2048;

export interface InlineImagesResult {
  /** Put every mutated attribute back exactly as it was. Idempotent. */
  restore: () => void;
  /** Absolute URLs we could not turn into a data: URL. */
  failed: string[];
  /** How many references were successfully inlined (diagnostics only). */
  inlined: number;
}

interface Target {
  el: Element;
  /** Which attribute we rewrote, so restore() puts back the right one. */
  attr: 'href' | 'src';
  /** Namespace for the attribute (xlink for legacy SVG), else null. */
  ns: string | null;
  /** Original attribute value, or null when the attribute was absent. */
  original: string | null;
  /** Original srcset (<img> only) — cleared so it can't win over src. */
  originalSrcset: string | null;
  /** Absolute URL to resolve. */
  url: string;
}

/** Resolve against the document base so relative hrefs (SVG <image>
 *  exposes the raw attribute, unlike HTMLImageElement.src) still fetch. */
function absolutise(raw: string): string {
  try {
    return new URL(raw, document.baseURI).href;
  } catch {
    return raw;
  }
}

/**
 * Identify an image by its leading bytes rather than by Content-Type.
 * Files uploaded through the SP backend are stored under /uploads with
 * hashed, EXTENSIONLESS names, so Apache often serves them with no
 * Content-Type at all — FileReader would then hand us a
 * `data:application/octet-stream;...` URL, which no SVG renderer will
 * draw. Sniffing gets us the right type regardless, and returning null
 * for unrecognised bytes is what rejects HTML error pages.
 */
function sniffMimeType(bytes: Uint8Array): string | null {
  const b = bytes;
  if (b.length < 4) return null;
  if (b[0] === 0x89 && b[1] === 0x50 && b[2] === 0x4e && b[3] === 0x47) return 'image/png';
  if (b[0] === 0xff && b[1] === 0xd8 && b[2] === 0xff) return 'image/jpeg';
  if (b[0] === 0x47 && b[1] === 0x49 && b[2] === 0x46 && b[3] === 0x38) return 'image/gif';
  if (b[0] === 0x42 && b[1] === 0x4d) return 'image/bmp';
  if (b[0] === 0x00 && b[1] === 0x00 && b[2] === 0x01 && b[3] === 0x00) return 'image/x-icon';
  if (
    b.length >= 12 &&
    b[0] === 0x52 && b[1] === 0x49 && b[2] === 0x46 && b[3] === 0x46 &&
    b[8] === 0x57 && b[9] === 0x45 && b[10] === 0x42 && b[11] === 0x50
  ) {
    return 'image/webp';
  }
  // SVG is text: skip a BOM / leading whitespace, then look for an XML
  // declaration, a doctype, a comment or the <svg tag itself.
  let i = 0;
  if (b[0] === 0xef && b[1] === 0xbb && b[2] === 0xbf) i = 3;
  while (i < b.length && (b[i] === 0x20 || b[i] === 0x09 || b[i] === 0x0a || b[i] === 0x0d)) i++;
  const head = String.fromCharCode(...b.slice(i, i + 64)).toLowerCase();
  if (head.startsWith('<?xml') || head.startsWith('<!doctype svg') || head.startsWith('<svg') || head.startsWith('<!--')) {
    const whole = String.fromCharCode(...b.slice(i, i + 1024)).toLowerCase();
    if (whole.includes('<svg')) return 'image/svg+xml';
  }
  return null;
}

/** btoa over binary bytes, chunked so a large logo can't blow the
 *  argument limit of String.fromCharCode. */
function bytesToBase64(bytes: Uint8Array): string {
  const CHUNK = 0x8000;
  let binary = '';
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(binary);
}

/** Strategy 1 — fetch the bytes and wrap them ourselves. Preserves
 *  vector SVG logos and avoids a lossy re-encode. */
async function viaFetch(url: string): Promise<string | null> {
  try {
    // credentials:'include' because /uploads/... on the SP host can sit
    // behind the same session cookie the page was loaded with.
    const res = await fetch(url, { credentials: 'include', cache: 'force-cache' });
    if (!res.ok) return null;
    const buf = await res.arrayBuffer();
    if (buf.byteLength === 0) return null;
    const bytes = new Uint8Array(buf);
    const mime = sniffMimeType(bytes);
    if (!mime) return null; // not an image — e.g. a soft-404 HTML page
    return `data:${mime};base64,${bytesToBase64(bytes)}`;
  } catch {
    return null;
  }
}

/** Strategy 2 — load through the browser's IMAGE path (the one that
 *  already worked on screen) and raster it. Loses vector fidelity but
 *  it is the difference between a logo and a blank box. */
async function viaCanvas(url: string): Promise<string | null> {
  try {
    const img = await new Promise<HTMLImageElement | null>((resolve) => {
      const el = new Image();
      // Deliberately NOT setting crossOrigin: for a same-origin logo it
      // is unnecessary, and for a cross-origin host without CORS headers
      // it would turn a working load into a failed one.
      el.onload = () => resolve(el);
      el.onerror = () => resolve(null);
      el.src = url;
    });
    if (!img) return null;
    const w = img.naturalWidth || img.width;
    const h = img.naturalHeight || img.height;
    if (!w || !h) return null; // e.g. an SVG with no intrinsic size
    const scale = Math.min(1, MAX_RASTER_PX / Math.max(w, h));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(w * scale));
    canvas.height = Math.max(1, Math.round(h * scale));
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    // Throws SecurityError when the canvas is tainted by a cross-origin
    // image served without CORS headers — the one case we can't rescue.
    return canvas.toDataURL('image/png');
  } catch {
    return null;
  }
}

async function resolveToDataUrl(url: string): Promise<string | null> {
  return (await viaFetch(url)) ?? (await viaCanvas(url));
}

/**
 * Rewrite every external image reference under `root` to a data: URL.
 * Always returns a result — this must never be the reason an export
 * fails, so an unresolvable image is left exactly as it was (letting
 * html-to-image fall back to its transparent placeholder) and simply
 * reported.
 */
export async function inlineImagesForCapture(root: Element): Promise<InlineImagesResult> {
  const targets: Target[] = [];

  // SVG <image> — note getAttribute, NOT href.baseVal: we want the raw
  // (possibly relative) value so we can resolve it ourselves.
  for (const el of Array.from(root.querySelectorAll('image'))) {
    const plain = el.getAttribute('href');
    const xlink = el.getAttributeNS(XLINK_NS, 'href');
    const raw = plain ?? xlink ?? '';
    if (!raw || raw.startsWith('data:')) continue;
    targets.push({
      el,
      attr: 'href',
      ns: plain !== null ? null : XLINK_NS,
      original: plain ?? xlink,
      originalSrcset: null,
      url: absolutise(raw),
    });
  }

  // HTML <img> — anything rendered inside a foreignObject or a custom
  // symbol's HTML content.
  for (const el of Array.from(root.querySelectorAll('img'))) {
    const raw = el.getAttribute('src') ?? '';
    if (!raw || raw.startsWith('data:')) continue;
    targets.push({
      el,
      attr: 'src',
      ns: null,
      original: raw,
      originalSrcset: el.getAttribute('srcset'),
      url: absolutise(raw),
    });
  }

  if (targets.length === 0) {
    return { restore: () => {}, failed: [], inlined: 0 };
  }

  // The same brand logo appears once per BOM row — resolve each URL
  // once and share the result across every element that uses it.
  const unique = Array.from(new Set(targets.map((t) => t.url)));
  const resolved = new Map<string, string | null>();
  await Promise.all(
    unique.map(async (url) => {
      resolved.set(url, await resolveToDataUrl(url));
    }),
  );

  const applied: Target[] = [];
  const failed: string[] = [];

  for (const t of targets) {
    const dataUrl = resolved.get(t.url);
    if (!dataUrl) {
      if (!failed.includes(t.url)) failed.push(t.url);
      continue;
    }
    try {
      if (t.ns) {
        t.el.setAttributeNS(t.ns, 'xlink:href', dataUrl);
      } else {
        t.el.setAttribute(t.attr, dataUrl);
      }
      // A stale srcset would out-rank the src we just rewrote.
      if (t.originalSrcset !== null) t.el.removeAttribute('srcset');
      applied.push(t);
    } catch {
      if (!failed.includes(t.url)) failed.push(t.url);
    }
  }

  let restored = false;
  const restore = () => {
    if (restored) return;
    restored = true;
    for (const t of applied) {
      try {
        if (t.ns) {
          if (t.original === null) t.el.removeAttributeNS(t.ns, 'href');
          else t.el.setAttributeNS(t.ns, 'xlink:href', t.original);
        } else if (t.original === null) {
          t.el.removeAttribute(t.attr);
        } else {
          t.el.setAttribute(t.attr, t.original);
        }
        if (t.originalSrcset !== null) t.el.setAttribute('srcset', t.originalSrcset);
      } catch {
        /* restoring must never throw — the export is already done */
      }
    }
  };

  return { restore, failed, inlined: applied.length };
}
